import { Grid, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import {
  DefaultCard,
  PaddedContent,
  moneyFormat,
  useControlledExpandBehavior,
} from 'design-system';
import React, { useMemo } from 'react';

// Data Layer
import { quotations } from 'data-layer-cr/dist/functions';
import {
  regularCoverages,
  specialCoverages,
} from 'data-layer-cr/dist/functions/coverages';

// Local functions
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import { mapUnitsToUniqueId } from '../../../../../../../functions/quotationMethods';

// Components
import {
  CustomerDetailsCard,
  DriverDetailsCard,
  SummaryCarDetailsCard,
} from '../../../../components';
import CoverageDetailList from '../../../../components/CoverageDetailList';

// Shared props
import DetailsContentProps from '../DetailsContentProps';

const { formatMoney } = moneyFormat;

interface MultipleOptionVehicleRCProps extends DetailsContentProps {
  differentDriver: boolean;
  currentProfile: PricingProfileResponse;
}
const MultipleOptionVehicleRC = ({
  quotation,
  coverageCatalogs,
  differentDriver,
  currentProfile,
}: MultipleOptionVehicleRCProps): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isExpanded, toggleExpandStatus } = useControlledExpandBehavior({
    defaultExpanded: '',
  });

  const mappedUnits = useMemo(() => mapUnitsToUniqueId(quotation), [quotation]);
  const coverageSection = useMemo(
    () =>
      mappedUnits.map((mappedUnit): React.ReactNode => {
        const { total, coverages } = mappedUnit.unit;
        const unitPackage = mappedUnit.unit.package;
        return (
          <Grid key={mappedUnit.id} item xs={12} md={4}>
            <DefaultCard
              data-testid={`card-${mappedUnit.id}`}
              title={`${unitPackage.description} ${formatMoney(
                total.grandTotal,
              )}`}
              collapsible={isMobile}
              forceCollapsed={!isExpanded(mappedUnit.id)}
              onForceCollapsedUpdate={(): void =>
                toggleExpandStatus(mappedUnit.id)
              }
            >
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="body2">Coberturas</Typography>
                </Grid>
                <Grid item xs={12}>
                  <CoverageDetailList
                    specialCoverages={specialCoverages(coverageCatalogs)}
                    coverages={regularCoverages(coverageCatalogs)}
                    simplifiedCoverages={coverages}
                    currentProfile={currentProfile}
                  />
                </Grid>
              </Grid>
            </DefaultCard>
          </Grid>
        );
      }),
    [
      mappedUnits,
      coverageCatalogs,
      isExpanded,
      toggleExpandStatus,
      isMobile,
      currentProfile,
    ],
  );

  const driver = useMemo(
    () => quotations.defaultUnitDriver(quotation),
    [quotation],
  );

  // only display driverDetailsCard if driver and customers are different or customerType is Legal Entity
  const displayDriverCard = useMemo(
    () => differentDriver || quotation.customerType === 'J',
    [differentDriver, quotation],
  );

  return (
    <PaddedContent>
      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        {/* On desktop, the coverage layout should display on top */}
        {!isMobile && (
          <Grid data-testid="coverage-desktop" item xs={12}>
            <Grid container spacing={2}>
              {coverageSection}
            </Grid>
          </Grid>
        )}

        {/* Customer information */}
        <Grid item xs={12} md={4}>
          <CustomerDetailsCard
            data-testid="customer-section"
            customerType={quotation.customerType}
            customer={quotation.customer}
            legalEntity={quotations.validLegalEntity(quotation)}
          />
        </Grid>
        {displayDriverCard && (
          <Grid item xs={12} md={4}>
            <DriverDetailsCard
              data-testid="driver-section"
              firstName={driver.firstName}
              lastName={driver.lastName}
              gender={driver.gender}
              birthdate={driver.birthday}
            />
          </Grid>
        )}
        {/* Car information */}
        <Grid item xs={12} md={4}>
          <SummaryCarDetailsCard
            data-testid="car-section"
            quotationSummary={quotation.summary}
          />
        </Grid>

        {/* On mobile, the coverage layout should display below the content */}
        {isMobile && (
          <Grid data-testid="coverage-mobile" item xs={12}>
            <Grid container spacing={2}>
              {coverageSection}
            </Grid>
          </Grid>
        )}
      </Grid>
    </PaddedContent>
  );
};

export default MultipleOptionVehicleRC;
